import React, { useCallback, useMemo, useState } from "react";
import Image from "../../../atoms/image/Image";
import VerticalGroup from "../../../atoms/verticalgroup/VerticalGroup";
import ResultsText from "../results-text/ResultsText";
import ResultsButton from "../results-button/ResultsButton";
import { useResultsContext } from "../context/context";
import { useKeypress } from "../../../../hooks/useKeypress";
import DynamicResultsText from "../results-text/DynamicResultsText";
import InlineGroup from "../../../atoms/inlinegroup/InlineGroup";
import Clickable from "../../../atoms/clickable/Clickable";
import Icon from "../../../atoms/icon/Icon";
import RoundResultsFontMultiplierModal from "../components/RoundResultsFontMultiplierModal";

interface Props {
  round: number;
  handleClick: () => void;
  logo?: string;
  gameName?: string;
}

const RoundResultsSummary: React.FC<Props> = ({
  round,
  handleClick,
  logo,
  gameName,
}) => {
  const [textSizeModalOpen, setTextSizeModalOpen] = useState(false);
  const context = useResultsContext();

  const onEscClick = useCallback(() => {
    context.exitPresentationMode();
  }, [context]);

  const handlePresentationModeClick = useCallback(() => {
    context.enterPresentationMode();
  }, [context]);

  const keymap: Record<string, () => void> = useMemo(() => {
    if (context.ignoreKeyPress) {
      return {} as Record<string, () => void>;
    }
    return {
      ArrowDown: handleClick,
      Escape: onEscClick,
    };
  }, [context.ignoreKeyPress, handleClick, onEscClick]);

  useKeypress({ keymap });

  return (
    <VerticalGroup full verticalCenter center wide>
      <Image
        src={logo}
        alt="default logo"
        dynamicSize={{ min: "50px", preferred: "6vw", max: "400px" }}
        className="logo"
      ></Image>
      <ResultsText size="lg" bold center colour="white">
        {gameName}
      </ResultsText>
      <DynamicResultsText size="xl" className="mt-8 mb-4" bold colour="white">
        ROUND {round} SUMMARY
      </DynamicResultsText>
      {!context.hidePagination && (
        <VerticalGroup spaceBetweenElements={4} center>
          <ResultsButton bold onClick={handleClick}>
            {" "}
            SEE RESULTS
          </ResultsButton>
          {context.allowPresentationMode && (
            <ResultsButton bold onClick={handlePresentationModeClick}>
              PRESENTATION MODE
            </ResultsButton>
          )}
          {textSizeModalOpen && (
            <RoundResultsFontMultiplierModal
              isOpen={textSizeModalOpen}
              onClose={() => setTextSizeModalOpen(false)}
            />
          )}
          <InlineGroup spaceBetweenElements={4} verticalCenter>
            {context.allowPresentationMode && (
              <Clickable onClick={() => setTextSizeModalOpen(true)}>
                <Icon
                  type="textSize"
                  colour="white"
                  size={6}
                  tip={{ content: "Change text size", id: "tezt-size" }}
                />
              </Clickable>
            )}
            {context.allowPresentationMode && (
              <Clickable
                onClick={() => context.updateCastState({ cast: !context.cast })}
              >
                <Icon
                  type="cast"
                  colour={context.cast ? "secondaryBlue" : "white"}
                  size={6}
                  tip={{ content: "Cast to participants", id: "cast" }}
                />
              </Clickable>
            )}
          </InlineGroup>
        </VerticalGroup>
      )}
    </VerticalGroup>
  );
};

export default RoundResultsSummary;
